import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  InvoiceState,
  clearInvoiceSearchResult,
  getInvoice,
  getProviderList,
  invoiceStateSelector,
} from '@patient-ui/patient-web/store';
import {
  FORM_ERROR_MESSAGES,
  ORBI_FIND_A_BILL_DOB_LABEL,
  ORBI_FIND_A_BILL_ERROR_CONTACT_AFTER,
  ORBI_FIND_A_BILL_ERROR_CONTACT_BEFORE,
  ORBI_FIND_A_BILL_ERROR_CONTACT_PHONE,
  ORBI_FIND_A_BILL_ERROR_DISMISS_LABEL,
  ORBI_FIND_A_BILL_ERROR_INTRO,
  ORBI_FIND_A_BILL_ERROR_REASONS,
  ORBI_FIND_A_BILL_ERROR_TITLE,
  ORBI_FIND_A_BILL_INSTRUCTIONS,
  ORBI_FIND_A_BILL_INSURANCE_LABEL,
  ORBI_FIND_A_BILL_INVOICE_LABEL,
  ORBI_FIND_A_BILL_PAY_LABEL,
  ORBI_FIND_A_BILL_TECH_ERROR,
  ORBI_FIND_A_BILL_TITLE,
  ORBI_FIND_A_BILL_ZERO_BALANCE,
  ORBI_FIND_A_BILL_ZIP_LABEL,
  ZIP_REGEX,
} from '@patient-ui/shared/constants';
import { RequestStatus } from '@patient-ui/shared/enums';
import { IInvoiceSearch, Invoice } from '@patient-ui/shared/models';

import { dateValidator } from '../../../shared/validators/date.validator';

/** Which action the user submitted the lookup with (AC 6). */
export type OrbiFindABillAction = 'payMyBill' | 'updateInsurance';

/**
 * A bill the card successfully located, plus the action to continue with
 * (PCSA-13419 AC 8). The card owns the LOOKUP; the host thread owns the routing
 * and closing the panel, the same split every other Orbi card uses.
 */
export interface OrbiFindABillFound {
  action: OrbiFindABillAction;
  /** The located invoice — the host seeds the destination page from it. */
  invoice: Invoice;
  /**
   * Whether the bill is payable now (`open`) or already has a payment in flight
   * (`pending`). The Billing page distinguishes these and sends `pending` to the
   * confirmation page instead of the checkout — a bill that is already being
   * paid must not be offered for payment again. The card reports which it found
   * and lets the host route, because routing is the host's job here.
   */
  status: 'open' | 'pending';
}

/**
 * Renders the Find a Bill card in the Orbi chat thread (PCSA-13419).
 *
 * A `FindABill` turn means the Billing agent could NOT match a bill to the
 * signed-in account, so instead of a dead end the card asks for the three
 * identifiers the invoice lookup needs — invoice number, billing ZIP, patient
 * date of birth (AC 3) — and runs the SAME lookup the portal's Billing page runs
 * (`getInvoice` + `getProviderList`, then `invoiceStateSelector`). Reusing that
 * flow rather than adding a second one is what makes a bill found from the chat
 * behave identically to the same bill found from the Billing page, including its
 * duplicate/zero-balance/pending edge cases.
 *
 * There is no mobile counterpart to mirror: mobile's Orbi handles no billing
 * return type at all and its one billing touchpoint SSOs into this portal, so
 * `PortalDashboardInvoicesSearchComponent` is the parity target — its form
 * shape, validators, validation copy and failure copy are reused verbatim.
 *
 * `data.invoices[0]` PREFILLS the invoice number when the agent sent one and the
 * field stays editable; an empty array simply leaves it blank (AC 4).
 *
 * Either action can be pressed at any time: AC 7 requires that pressing one with
 * criteria missing SHOW a message beneath each missing field, which a disabled
 * button cannot do because it never delivers the click. The guard is in
 * `onSubmit` instead, so nothing is requested until the form is valid. On success
 * the card EMITS `found` for the thread to route (AC 8). On failure it stays put,
 * shows the approved message and keeps every entry (AC 9) — it never swaps in a
 * different bill (AC 10), because the only invoice it can emit is the one the
 * lookup returned for the criteria the user typed.
 */
@Component({
  selector: 'patient-ui-orbi-find-a-bill-card',
  templateUrl: './orbi-find-a-bill-card.component.html',
  styleUrls: ['./orbi-find-a-bill-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrbiFindABillCardComponent implements OnInit, OnDestroy {
  /**
   * Invoice numbers from the turn's `data.invoices` (AC 4). May be absent or
   * empty — `FindABill` is allowed to send none.
   */
  @Input() invoiceNumbers: string[] | null = null;

  /** Emitted once a bill is located, for the thread to route into the flow. */
  @Output() found = new EventEmitter<OrbiFindABillFound>();

  /** Card copy. */
  readonly title = ORBI_FIND_A_BILL_TITLE;
  readonly instructions = ORBI_FIND_A_BILL_INSTRUCTIONS;
  readonly invoiceLabel = ORBI_FIND_A_BILL_INVOICE_LABEL;
  readonly zipLabel = ORBI_FIND_A_BILL_ZIP_LABEL;
  readonly dobLabel = ORBI_FIND_A_BILL_DOB_LABEL;
  readonly payLabel = ORBI_FIND_A_BILL_PAY_LABEL;
  readonly insuranceLabel = ORBI_FIND_A_BILL_INSURANCE_LABEL;
  readonly errorTitle = ORBI_FIND_A_BILL_ERROR_TITLE;
  readonly errorIntro = ORBI_FIND_A_BILL_ERROR_INTRO;
  readonly errorReasons = ORBI_FIND_A_BILL_ERROR_REASONS;
  /**
   * The contact line in three parts so the phone number can be its own node — a
   * `tel:` link, at every breakpoint (PCSA-13562; mobile-only from PCSA-13312,
   * which paired the anchor with a plain-text span from md up). The parts are
   * what compose `ORBI_FIND_A_BILL_ERROR_CONTACT`, so the rendered sentence is
   * still character-for-character the approved copy.
   */
  readonly errorContactBefore = ORBI_FIND_A_BILL_ERROR_CONTACT_BEFORE;
  readonly errorContactPhone = ORBI_FIND_A_BILL_ERROR_CONTACT_PHONE;
  readonly errorContactAfter = ORBI_FIND_A_BILL_ERROR_CONTACT_AFTER;
  /** Derived from the number itself so the two can never disagree. */
  readonly errorContactPhoneHref = `tel:${ORBI_FIND_A_BILL_ERROR_CONTACT_PHONE}`;
  readonly errorDismissLabel = ORBI_FIND_A_BILL_ERROR_DISMISS_LABEL;

  /**
   * Field validation copy — the Billing page's own messages, so a mistyped ZIP
   * reads the same in the chat as it does there.
   */
  readonly validationMessages = {
    invoiceNumber: {
      required: FORM_ERROR_MESSAGES.invoice.required.invoiceNumber,
    },
    zipCode: {
      required: FORM_ERROR_MESSAGES.invoice.required.zipCode,
      pattern: FORM_ERROR_MESSAGES.address.zipCode.pattern,
    },
    dateOfBirth: {
      required: FORM_ERROR_MESSAGES.invoice.required.dateOfBirth,
      pattern: FORM_ERROR_MESSAGES.date.pattern,
      invalidDate: FORM_ERROR_MESSAGES.date.invalidDate,
      invalidYear: FORM_ERROR_MESSAGES.date.invalidYear,
      mask: FORM_ERROR_MESSAGES.date.mask,
    },
  };

  searchForm!: UntypedFormGroup;

  /**
   * Which failure the card is showing, or null for the form (AC 9). The three
   * conditions are the Billing page's own, minus `duplicateInvoiceFlag` — that
   * one renders the same heading + contact line as `searchCriteria` without the
   * bulleted causes, which `errorReasons` already covers by being skipped.
   */
  errorCondition: 'searchCriteria' | 'techDifficulties' | 'zeroBalance' | null =
    null;

  /** True while a lookup is in flight, per action (drives the button spinner). */
  payInProgress = false;
  insuranceInProgress = false;

  /** The oldest date of birth the lookup accepts (120 years, portal parity). */
  private readonly maxDate = new Date();
  private readonly minDate = new Date();

  /** The action the in-flight lookup was started with. */
  private pendingAction: OrbiFindABillAction | null = null;

  /** Set once a lookup has resolved, so late state emissions are ignored. */
  private searchDone = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly builder: UntypedFormBuilder,
    private readonly store: Store<InvoiceState>,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.minDate.setFullYear(this.minDate.getFullYear() - 120);

    // `updateOn: 'blur'` matches the Billing page: the date validator rejects a
    // half-typed date, so validating per keystroke would flag every field the
    // moment it is touched.
    this.searchForm = this.builder.group(
      {
        invoiceNumber: [this.prefilledInvoiceNumber, [Validators.required]],
        zipCode: ['', [Validators.required, Validators.pattern(ZIP_REGEX)]],
        dateOfBirth: [
          '',
          [Validators.required, dateValidator(this.maxDate, this.minDate)],
        ],
      },
      { updateOn: 'blur' }
    );

    // Drop any invoice a previous search left in the store, so a stale hit can
    // never be mistaken for this card's result (AC 10).
    this.store.dispatch(clearInvoiceSearchResult());

    this.store
      .select(invoiceStateSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (state) => this.onInvoiceState(state),
        error: () => this.fail('techDifficulties'),
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get formControls(): UntypedFormGroup['controls'] {
    return this.searchForm.controls;
  }

  /**
   * The heading inside the failure alert. All three conditions share the alert
   * itself — the Figma draws one error treatment, and an outage or a settled bill
   * is no less an error than a bad invoice number — but each states its own cause,
   * and only `searchCriteria` has causes worth enumerating underneath.
   */
  get alertHeading(): string {
    switch (this.errorCondition) {
      case 'techDifficulties':
        return ORBI_FIND_A_BILL_TECH_ERROR;
      case 'zeroBalance':
        return ORBI_FIND_A_BILL_ZERO_BALANCE;
      default:
        return this.errorTitle;
    }
  }

  /** True while either lookup is in flight — both buttons disable together. */
  get isSearching(): boolean {
    return this.payInProgress || this.insuranceInProgress;
  }

  /**
   * Run the lookup for one of the two actions (AC 6/7). An invalid form marks
   * every control dirty so each missing field shows its own message, and no
   * request is sent — that is what "cannot continue until the information has
   * been provided" means here, alongside the disabled buttons.
   */
  onSubmit(action: OrbiFindABillAction): void {
    if (!this.searchForm.valid) {
      Object.keys(this.formControls).forEach((key) =>
        this.formControls[key].markAsDirty()
      );
      this.searchForm.updateValueAndValidity();
      return;
    }

    this.errorCondition = null;
    this.pendingAction = action;
    this.searchDone = false;
    this.payInProgress = action === 'payMyBill';
    this.insuranceInProgress = action === 'updateInsurance';

    const payload = this.searchForm.getRawValue() as IInvoiceSearch;
    payload.invoiceNumber = payload.invoiceNumber?.trim();
    payload.dateOfBirth = OrbiFindABillCardComponent.formatDate(
      this.searchForm.get('dateOfBirth')?.value
    );

    // The provider list is what the insurance flow needs downstream; the Billing
    // page requests both together, so the two destinations behave the same.
    this.store.dispatch(getInvoice({ payload }));
    this.store.dispatch(getProviderList());
  }

  /**
   * Dismiss the failure and return to the form (AC 9 / Figma "Go Back to
   * Review"). The form group is never rebuilt, so every entry is still there.
   */
  onDismissError(): void {
    this.errorCondition = null;
  }

  /**
   * The invoice number to prefill (AC 4.1/4.2). Only the FIRST entry is used:
   * the card searches one bill at a time, and `data.invoices` is a list only
   * because the billing contract shares one shape across its return types.
   */
  private get prefilledInvoiceNumber(): string {
    return this.invoiceNumbers?.[0]?.trim() ?? '';
  }

  /**
   * React to the shared invoice state. Mirrors the Billing page's own reading of
   * it, so the same backend outcome produces the same user-visible result:
   * a failed request or an empty response is a criteria failure, a located
   * invoice with a real balance continues, and a zero balance stops with its own
   * message rather than routing to a payment page with nothing to pay.
   */
  private onInvoiceState(state: InvoiceState): void {
    if (!this.pendingAction || this.searchDone) {
      return;
    }

    if (
      state.getInvoiceRequestStatus === RequestStatus.Failure ||
      state.getProviderListRequestStatus === RequestStatus.Failure
    ) {
      // A duplicate-invoice flag is a "not payable online" case, not an outage —
      // the same heading and contact line apply.
      this.fail(
        state.selectDuplicateInvoice ? 'searchCriteria' : 'techDifficulties'
      );
      return;
    }

    if (
      state.getInvoiceRequestStatus === RequestStatus.SuccessButEmptyResponse
    ) {
      this.fail('searchCriteria');
      return;
    }

    // Both requests must have landed before the result is trusted: `getInvoice`
    // alone can be Success while the provider list is still Pending, and the
    // insurance destination needs it.
    if (
      state.getInvoiceRequestStatus !== RequestStatus.Success ||
      state.getProviderListRequestStatus !== RequestStatus.Success ||
      !state.invoice?.number
    ) {
      return;
    }

    const status = OrbiFindABillCardComponent.paymentStatus(state);
    if (status === 'zeroBalance') {
      this.fail('zeroBalance');
      return;
    }

    this.searchDone = true;
    const action = this.pendingAction;
    this.stopSearching();
    this.found.emit({ action, invoice: state.invoice, status });
  }

  /**
   * Classify the located invoice exactly as the Billing page does, so the chat
   * never sends a bill somewhere the Billing page would not: a credit balance
   * and a settled bill are both "nothing to pay", while a zero balance that has
   * transaction ids means a payment is already in flight.
   */
  private static paymentStatus(
    state: InvoiceState
  ): 'open' | 'pending' | 'zeroBalance' {
    if (state.invoice.balanceDue < 0) {
      return 'zeroBalance';
    }
    if (state.invoice.balanceDue === 0) {
      return state.invoice.transactionIds?.length ? 'pending' : 'zeroBalance';
    }
    return 'open';
  }

  /** Show a failure, keep the form as typed (AC 9). */
  private fail(
    condition: 'searchCriteria' | 'techDifficulties' | 'zeroBalance'
  ): void {
    this.searchDone = true;
    this.errorCondition = condition;
    this.stopSearching();
    this.cdr.markForCheck();
  }

  private stopSearching(): void {
    this.payInProgress = false;
    this.insuranceInProgress = false;
    this.pendingAction = null;
  }

  /**
   * `MMDDYYYY` (what the mask yields once its separators are stripped) to the
   * `YYYY-MM-DD` the invoice API expects — the Billing page's own conversion.
   */
  private static formatDate(dateString: string): string {
    return dateString
      ? `${dateString.substring(4)}-${dateString.substring(
          0,
          2
        )}-${dateString.substring(2, 4)}`
      : '';
  }
}
